import { useMemo, useState, useEffect } from "react";
import { T, UI, FONT } from "../constants/tokens";
import { fmtPct, shownMoneyDir } from "../utils/formatters";
import Money from "./Money";
import { useFxRates } from "../utils/fxRates";
import { officialDocsFor, useTodayNews, dateTime, DAY_MS } from "../utils/todayMoves";
import { fetchGeoEvents } from "../utils/geoEvents";
import { quotedHeadline } from "../utils/voice";
import Headline from "./Headline";
import { isHebrew, waitingNote } from "../utils/headline";
import OfficialBadge from "./OfficialBadge";
import LTR from "./LTR";
import { isAdvisoryHeadline } from "../constants/advisory";
import { Skeleton as Skel, Loading } from "./Skeleton";
import DualMoney from "./DualMoney";

// ─── מה קרה היום · what was published, beside the number ─────────────────────
// How a holding moved today, in dollars, and what named sources published
// about the company in the last 24 hours. The number is ours and exact; the articles are theirs. The
// two sit side by side and no string on this screen joins them with a cause
// — see utils/todayMoves.js for the rule and why it is the legal boundary.

const NO_NEWS = "אין כתבות על החברה ב-7 הימים האחרונים";
// The holding panel's window (21/10/2026 · it was "today").
const NEWS_DAYS = 7;
const NEWS_LABEL = "כתבות על החברה היום:";


// ±$ and ±% for one holding or the whole portfolio. Gain/loss colour: this
// is the user's own money moving, the one thing green and red are for.
export function MoveFigures({ dollar, pct, cur, size = 15, weight = 700, whole = false }) {
  // Colour by the dollar figure as shown, sign glued to the currency, the
  // percent through the one percent rule (sprint 17).
  useFxRates();   // re-render when a rate arrives · the colour reads it
  const dir = shownMoneyDir(dollar, cur, whole ? 0 : null);
  const color = dir > 0 ? UI.gain : dir < 0 ? UI.loss : UI.text.primary;
  return (
    <LTR className="si-num" style={{ display: "inline-flex", alignItems: "baseline", gap: 6, fontSize: size, fontWeight: weight, color }}>
      <Money value={dollar} cur={cur} size={size} weight={weight} color={color} signed decimals={whole ? 0 : null}/>
      <span style={{ fontSize: Math.max(11, size - 3), fontWeight: 600 }}>{fmtPct(pct)}</span>
    </LTR>
  );
}

// One published item — a Federal Register document or a news article. Text
// only: the app does not navigate out; the source and the time are the
// attribution, and the headline is rendered as a quotation of that source.
function PublishedRow({ doc, article, title }) {
  if (doc) {
    const q = quotedHeadline(doc);
    // Hebrew only (26/10/2026) · a document without its Hebrew title waits.
    if (!q || !q.rtl) return null;
    const t = doc.published_at ? new Date(doc.published_at).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" }) : null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <OfficialBadge official/>
          {doc.source_name && <LTR style={{ fontFamily: FONT.latin, fontSize: 12, color: UI.text.secondary, fontWeight: 700 }}>{doc.source_name}</LTR>}
          {t && <span style={{ fontSize: 12, color: UI.text.muted }}>· {t}</span>}
        </div>
        <Headline text={q.text} style={{ fontSize: 14, fontWeight: 600, color: UI.text.primary, lineHeight: 1.5 }}/>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Hebrew only (26/10/2026): the list passes an article here once its
          Hebrew headline is in. */}
      <Headline text={title} style={{ fontSize: 14, fontWeight: 600, color: UI.text.primary, lineHeight: 1.5 }}/>
      <div style={{ fontSize: 12, color: UI.text.muted, display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
        {article.source && <LTR style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.secondary }}>{article.source}</LTR>}
        {article.datetime > 0 && <span>· <LTR>{dateTime(article.datetime)}</LTR></span>}
      </div>
    </div>
  );
}

// What was published about one company today: official documents first, then
// up to three articles; a skeleton while loading; the stated absence when none.
// An article appears once its Hebrew headline is in (26/10/2026): until then
// it is counted in one quiet line, and the list fills in when the server's
// translation arrives (utils/headline.js, useHebrewNews).
export function PublishedList({ docs, articles, titles, ready = true }) {
  if (articles === null || articles === undefined || (articles.length && !ready)) {
    return (
      <Loading style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Skel w="85%" h={13}/><Skel w="40%" h={11}/>
      </Loading>
    );
  }
  const shown = articles.filter(a => {
    const t = titles[a.id || a.url];
    // The translation is checked as well as the English (constants/advisory).
    return typeof t === "string" && isHebrew(t) && !isAdvisoryHeadline(t);
  });
  const waiting = articles.filter(a => typeof titles[a.id || a.url] !== "string").length;
  const items = [
    ...docs.map(d => ({ key: `doc:${d.id}`, doc: d })),
    ...shown.map(a => ({ key: a.id || a.url, article: a })),
  ];
  if (!items.length && !waiting) return <div style={{ fontSize: 13, color: UI.text.muted }}>{NO_NEWS}</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted }}>{NEWS_LABEL}</div>
      {items.map(it => (
        <PublishedRow key={it.key} doc={it.doc} article={it.article} title={it.article ? titles[it.article.id || it.article.url] : null}/>
      ))}
      {waitingNote(waiting) && <div style={{ fontSize: 12, color: UI.text.muted }}>{waitingNote(waiting)}</div>}
    </div>
  );
}

// ─── Holding panel: one company ─────────────────────────────────────────────
export function HoldingToday({ symbol, name, shares, currentPrice, prevClose, geoEvents, cur = "USD" }) {
  const move = useMemo(() => {
    if (!(shares > 0) || !(currentPrice > 0) || !(prevClose > 0)) return null;
    return { dollar: (currentPrice - prevClose) * shares, pct: ((currentPrice - prevClose) / prevClose) * 100 };
  }, [shares, currentPrice, prevClose]);
  const syms  = useMemo(() => [symbol], [symbol]);
  const names = useMemo(() => ({ [symbol]: name && name !== symbol ? name : "" }), [symbol, name]);
  const { news, titles, ready } = useTodayNews(syms, { names, days: NEWS_DAYS, perSymbol: 3 });
  // The panel has no geo feed of its own; one 48-hour read, same as the home
  // screen, so an official document about this company is not missed here.
  const [ownGeo, setOwnGeo] = useState(geoEvents ?? null);
  useEffect(() => {
    if (geoEvents !== undefined) { setOwnGeo(geoEvents); return; }
    let alive = true;
    fetchGeoEvents().then(evs => { if (alive) setOwnGeo(evs); }).catch(() => { if (alive) setOwnGeo([]); });
    return () => { alive = false; };
  }, [geoEvents]);

  return (
    <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: UI.text.muted }}>מה קרה היום</span>
        {move
          ? <DualMoney usd={move.dollar} cur={cur} size={17} signed color={move.dollar >= 0 ? UI.gain : UI.loss} style={{ alignItems: "flex-end" }}>
              <MoveFigures dollar={move.dollar} pct={move.pct} cur={cur} size={17} weight={800}/>
            </DualMoney>
          : <span style={{ fontSize: 13, color: UI.text.muted }}>אין ציטוט עדכני</span>}
      </div>
      <div style={{ marginTop: 12 }}>
        <PublishedList docs={officialDocsFor(ownGeo, symbol, NEWS_DAYS * DAY_MS, names[symbol])} articles={news[symbol]} titles={titles} ready={ready}/>
      </div>
    </div>
  );
}
